import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import dayjs from "dayjs";
import api from "../../services/api";
import AssigneeDropdown from "../../utils/AssigneeDropdown";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { getStatusMeta } from "../../utils/StatusChip";
import { sortTasks } from "../../utils/taskSort";
import { useTaskOverlay, taskHighlightSx } from "../../utils/TaskOverlayContext";

/**
 * Kanban columns mirror the status options used in the list/grid views so a
 * task can move between every state the rest of the app understands.
 */
const STATUS_COLUMNS = [
  "To Start",
  "Doing",
  "On hold",
  "Task Complete",
  "Documented",
  "Closed w Review",
];

const Board = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  alignItems: "flex-start",
  overflowX: "auto",
  paddingBottom: theme.spacing(1),
}));

const Column = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isOver",
})(({ theme, isOver }) => ({
  flex: "0 0 280px",
  width: 280,
  display: "flex",
  flexDirection: "column",
  backgroundColor: isOver
    ? theme.palette.action.hover
    : theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  maxHeight: "calc(100vh - 220px)",
}));

const ColumnHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== "accent",
})(({ theme, accent }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderTop: `3px solid ${accent}`,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const ColumnBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  overflowY: "auto",
  flex: 1,
  minHeight: 80,
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  cursor: "grab",
  borderLeft: `4px solid transparent`,
  "&:active": { cursor: "grabbing" },
  "&:hover": { boxShadow: theme.shadows[3] },
}));

/**
 * Drag-and-drop Kanban board grouping every task across projects/tracks by
 * status. Dropping a card in another column patches the task status, with an
 * optimistic update so the move is reflected immediately.
 *
 * @param {Object} props
 * @param {Array} props.projects - Nested projects → tracks → tasks
 * @param {function} props.fetchProjects - Refetches projects (used on error)
 * @param {function} props.setProjects - Updates projects state
 * @returns {React.ReactElement}
 */
export const KanbanView = ({
  projects = [],
  fetchProjects,
  setProjects,
  sortCriteria = [],
}) => {
  const { openTaskDetail, highlightTaskId } = useTaskOverlay();
  const { currentWorkspace } = useWorkspace();
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);

  // Flatten the nested structure into cards while keeping project/track context
  // so each card can show where the task lives.
  const cardsByStatus = useMemo(() => {
    const groups = {};
    STATUS_COLUMNS.forEach((status) => {
      groups[status] = [];
    });
    const other = [];

    (projects || []).forEach((project) => {
      (project.tracks || []).forEach((track) => {
        (track.tasks || []).forEach((task) => {
          const card = {
            ...task,
            projectName: project.name,
            projectEpic: project.epic,
            trackName: track.name,
          };
          if (groups[task.status]) {
            groups[task.status].push(card);
          } else {
            other.push(card);
          }
        });
      });
    });

    // Apply the shared sort within each status column.
    STATUS_COLUMNS.forEach((status) => {
      groups[status] = sortTasks(groups[status], sortCriteria);
    });

    return { groups, other };
  }, [projects, sortCriteria]);

  /**
   * Optimistically applies a single field change across the nested project tree.
   */
  const applyFieldLocally = useCallback(
    (taskId, field, value) => {
      const updated = (projects || []).map((project) => ({
        ...project,
        tracks: (project.tracks || []).map((track) => ({
          ...track,
          tasks: (track.tasks || []).map((task) =>
            task.id === taskId ? { ...task, [field]: value } : task
          ),
        })),
      }));
      setProjects(updated);
    },
    [projects, setProjects]
  );

  /**
   * Patches a single task field with an optimistic update and rollback.
   */
  const updateTaskField = useCallback(
    async (taskId, field, value) => {
      applyFieldLocally(taskId, field, value);
      try {
        await api.patch(`tasks/${taskId}/`, {
          [field]: value,
          workspace: currentWorkspace?.id,
        });
      } catch (error) {
        console.error("Error updating task:", error);
        const refreshed = await fetchProjects();
        if (Array.isArray(refreshed)) setProjects(refreshed);
      }
    },
    [applyFieldLocally, currentWorkspace, fetchProjects, setProjects]
  );

  const handleDrop = useCallback(
    async (status) => {
      setOverColumn(null);
      const taskId = draggedTaskId;
      setDraggedTaskId(null);
      if (!taskId) return;

      // Find the current status so we can skip no-op drops.
      let current;
      (projects || []).some((project) =>
        (project.tracks || []).some((track) =>
          (track.tasks || []).some((task) => {
            if (task.id === taskId) {
              current = task.status;
              return true;
            }
            return false;
          })
        )
      );
      if (current === status) return;

      updateTaskField(taskId, "status", status);
    },
    [draggedTaskId, projects, updateTaskField]
  );

  if (!Array.isArray(projects) || projects.length === 0) {
    return <Alert severity="info">No tasks available</Alert>;
  }

  const renderCard = (task) => (
    <Card
      key={task.id}
      elevation={1}
      draggable
      onDragStart={() => setDraggedTaskId(task.id)}
      onDragEnd={() => {
        setDraggedTaskId(null);
        setOverColumn(null);
      }}
      sx={{
        borderLeftColor: getStatusMeta(task.status).solid,
        ...(task.id === highlightTaskId ? taskHighlightSx : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, flex: 1, wordBreak: "break-word" }}
        >
          {task.name}
        </Typography>
        <Tooltip title="Open task">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openTaskDetail(task.id);
            }}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {task.projectName}
        {task.trackName ? ` · ${task.trackName}` : ""}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
        }}
      >
        <AssigneeDropdown
          currentSelected={task.assignee}
          compact
          onChange={(userId) => updateTaskField(task.id, "assignee", userId)}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {task.dueDate && (
            <Chip
              size="small"
              variant="outlined"
              label={dayjs(task.dueDate).format("MMM D")}
            />
          )}
          {(task.lineUp ?? null) !== null && (
            <Chip size="small" label={task.lineUp} />
          )}
        </Box>
      </Box>
    </Card>
  );

  return (
    <Board>
      {STATUS_COLUMNS.map((status) => {
        const meta = getStatusMeta(status);
        const cards = cardsByStatus.groups[status] || [];
        return (
          <Column
            key={status}
            elevation={0}
            isOver={overColumn === status}
            onDragOver={(e) => {
              e.preventDefault();
              if (overColumn !== status) setOverColumn(status);
            }}
            onDragLeave={(e) => {
              // Only clear when leaving the column, not its children.
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setOverColumn((prev) => (prev === status ? null : prev));
              }
            }}
            onDrop={() => handleDrop(status)}
          >
            <ColumnHeader accent={meta.solid}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {meta.label}
              </Typography>
              <Chip size="small" label={cards.length} />
            </ColumnHeader>
            <ColumnBody>
              {cards.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Drop tasks here
                </Typography>
              ) : (
                cards.map(renderCard)
              )}
            </ColumnBody>
          </Column>
        );
      })}
    </Board>
  );
};

export default KanbanView;
